import { useCallback, useState, useRef } from 'react';

import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';
import CopyOutlined from '@ant-design/icons/CopyOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import { Checkbox, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { ModalWarning, CharCount } from 'components/Presentational';
import { Button, Row, Col, Collapse, Typography } from 'components/Shared/Antd';
import { CustomButtonAntd } from 'components/Shared/CustomButtonAntd/style';
import Toast from 'components/Shared/Toast';
import format from 'date-fns/format';
import { isEmpty } from 'helpers/lodash';
import { useDidMountEffect } from 'hooks';
import { IEventForm, ILoteForm, ITicketForm } from 'interfaces/event';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import lotService from 'services/lot';
import { v4 } from 'uuid';

import { ticketLotDefault } from '../../mock';
import Lot from './Lot';
import * as S from './styles';

function formatNumber(number) {
  const decimalSeparator = ',';
  const fixedNumber = Number(number).toFixed(2);
  const [integerPart, decimalPart] = fixedNumber.split('.');

  const formattedIntegerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const formattedDecimalPart = decimalPart ? decimalSeparator + decimalPart : '';

  return formattedIntegerPart + formattedDecimalPart;
}

const { Title } = Typography;
const { Panel } = Collapse;

const CharCounter = ({ max, align, index }) => {
  const { control } = useFormContext();
  const ticketName = useWatch({ control: control, name: `ticket.${index}.name` });
  return <CharCount value={ticketName} align={align} max={max} />;
};

interface TicketProps {
  canBeDeleted: boolean;
  index: number;
  onDelete: (index: number, ticket: ITicketForm) => void;
  onDuplicate: (ticket: ITicketForm) => void;
  setActiveLots: (keys: string[]) => void;
  activeLots: string[];
}

const Ticket = ({ canBeDeleted, index, onDelete, onDuplicate, setActiveLots, activeLots }: TicketProps) => {
  const [openModalDelete, setOpenModalDelete] = useState<
    ILoteForm & { modal?: 'disable' | 'delete'; lotIndex: number }
  >(null);

  const {
    control,
    register,
    watch,
    getValues,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext<IEventForm>();

  const { append, fields, remove } = useFieldArray({
    control,
    name: `ticket.${index}.lot`,
    keyName: 'identificator'
  });
  const isPaidWatch = watch(`ticket.${index}.is_paid`);
  const showOnPageWatch = watch(`ticket.${index}.show_on_page`);
  const isUnlimetdWatch = watch(`ticket.${index}.is_unlimited`);

  const isAdding = useRef(false);

  const handleAddLot = () => {
    if (!isEmpty(window['formErrors'])) {
      Toast.error('Verifique todos os campos');
      return;
    }
    const ticketLotLength = getValues(`ticket.${index}.lot`).length;
    isAdding.current = true;
    append({ ...ticketLotDefault, identificator: v4(), name: `Lote ${ticketLotLength + 1}` });
  };

  useDidMountEffect(() => {
    if (isAdding.current) {
      const lastIndex = fields.length - 1;
      const lastLotKey = fields[lastIndex].identificator;
      setActiveLots([lastLotKey]);
      if (getValues(`ticket.${index}.lot.${lastIndex}.is_active`)) {
        trigger(`ticket.${index}.lot.${lastIndex}`);
      }
      isAdding.current = false;
    }
  }, [fields.length]);

  const handleDuplicate = () => {
    const ticketValues = getValues(`ticket.${index}`);

    onDuplicate(ticketValues);
  };

  const handleDeleteLot = (lotIndex: number, lot: ILoteForm) => {
    const modalDisable = !!lot.quantity_sold;
    setOpenModalDelete({ ...lot, lotIndex, modal: modalDisable ? 'disable' : 'delete' });
  };

  const removeLot = useCallback(() => {
    const { id, lotIndex } = openModalDelete;
    if (id) {
      lotService.delete(id).subscribe({
        next: () => {
          remove(lotIndex);
          Toast.show('Lote excluído com sucesso!');
        },
        error: ({ data: response }) => {
          if (response && response?.code === 'ERR_CUSTOM') {
            Toast.error(response.details);
          } else {
            Toast.error('Houve um erro ao excluir o lote');
          }
        }
      });
    } else {
      remove(lotIndex);
    }

    return setOpenModalDelete(null);
  }, [openModalDelete, remove]);

  const disableLot = useCallback(() => {
    setOpenModalDelete(null);
    const { id, lotIndex } = openModalDelete;
    if (id) {
      lotService.desactivate(id).subscribe({
        next: () => {
          setValue(`ticket.${index}.lot.${lotIndex}.dateEnd`, new Date());
          setValue(`ticket.${index}.lot.${lotIndex}.is_active`, 0);
          Toast.show('Lote desativado com sucesso');
        },
        error: ({ data: response }) => {
          if (response && response?.code === 'ERR_CUSTOM') {
            Toast.error(response.details);
          } else {
            Toast.error('Houve um erro ao desativar o lote');
          }
        }
      });
    }
  }, [index, openModalDelete, setValue]);

  return (
    <S.Wrapper is_active={!!getValues(`ticket.${index}.is_active`)}>
      <Grid container item justifyContent='space-between'>
        <Grid item xs={8}>
          <Typography.Text strong>Ingresso {index + 1}</Typography.Text>
        </Grid>

        <Grid container item justifyContent='end' alignItems='center' spacing={1} style={{ marginBottom: '16px' }}>
          <Grid item justifyContent='end'>
            <CustomButtonAntd onClick={handleDuplicate}>
              <CopyOutlined />
              Clonar Ingresso
            </CustomButtonAntd>
          </Grid>

          {canBeDeleted && (
            <Grid item>
              <S.CustomizedIconButton
                onClick={() => {
                  onDelete(index, getValues(`ticket.${index}`));
                }}
              >
                <DeleteOutlined style={{ color: 'rgba(0, 0, 0, 0.85)' }} />
              </S.CustomizedIconButton>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid container item spacing={3}>
        <Grid item xs={12} md={4}>
          <TextField
            error={!!errors.ticket?.[index]?.name}
            fullWidth
            helperText={errors.ticket?.[index]?.name?.message}
            id='ticket-name-text-field'
            InputLabelProps={{
              shrink: true
            }}
            label='Nome do ingresso'
            placeholder='Insira um nome para seu ingresso'
            variant='outlined'
            {...register(`ticket.${index}.name`)}
          />
          <CharCounter align='right' max={30} index={index} />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            error={!!errors.ticket?.[index]?.buy_minimum}
            fullWidth
            helperText={errors.ticket?.[index]?.buy_minimum?.message}
            id='min-buy-text-field'
            InputLabelProps={{
              shrink: true
            }}
            label='Min. de compra'
            type='number'
            variant='outlined'
            {...register(`ticket.${index}.buy_minimum`)}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <TextField
            error={!!errors.ticket?.[index]?.buy_limit}
            fullWidth
            helperText={errors.ticket?.[index]?.buy_limit?.message}
            id='max-buy-text-field'
            InputLabelProps={{
              shrink: true
            }}
            label='Max. de compra'
            placeholder='Insira uma quantidade máxima'
            type='number'
            variant='outlined'
            {...register(`ticket.${index}.buy_limit`)}
          />
        </Grid>
      </Grid>

      <Grid container item spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={isPaidWatch} {...register(`ticket.${index}.is_paid`)} />}
              label='Ingresso vai ser pago'
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={showOnPageWatch} {...register(`ticket.${index}.show_on_page`)} />}
              label='Mostrar na página do evento'
            />
          </FormGroup>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={isUnlimetdWatch} {...register(`ticket.${index}.is_unlimited`)} />}
              label='Os ingressos serão ilimitados'
            />
          </FormGroup>
        </Grid>
      </Grid>

      <Row style={{ padding: '12px 16px', marginTop: '32px' }}>
        <Col md={9} style={{ marginRight: '-16px' }}>
          <b>Lote</b>
        </Col>
        <Col md={3}>
          <b>Início</b>
        </Col>
        <Col md={3}>
          <b>Término</b>
        </Col>
        <Col md={3}>
          <b>Disponível</b>
        </Col>
        <Col md={3} style={{ marginRight: '-8px' }}>
          <b>Vendidos</b>
        </Col>
        <Col md={3} style={{ marginRight: '-12px' }}>
          <b>{'Valor (R$)'}</b>
        </Col>
      </Row>
      <Collapse activeKey={activeLots} expandIconPosition='end' onChange={setActiveLots}>
        {fields.map((item, lotIndex) => {
          const lotIndexError = window['formErrors']?.['ticket']?.[index]?.['lot']?.[lotIndex];

          return (
            <Panel
              header={
                <div>
                  <Row>
                    <Col md={9}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <div>{item.name}</div>
                        {!!lotIndexError && <CloseCircleOutlined style={{ color: 'red', fontSize: '14px' }} />}
                      </div>
                    </Col>
                    {item.dateStart && (
                      <>
                        <Col md={3}>{format(item.dateStart, 'dd/MM/yyyy')}</Col>
                        <Col md={3}>{format(item.dateEnd, 'dd/MM/yyyy')}</Col>
                        {item.quantity_available !== null && (
                          <Col md={3}>{item.quantity_available > 9000000 ? 'ilimitado' : item.quantity_available}</Col>
                        )}
                        {item.quantity_available === null && (
                          <Col md={3}>{item.quantity > 9000000 ? 'ilimitado' : item.quantity}</Col>
                        )}
                        <Col md={3}>{item.quantity_sold}</Col>
                        <Col md={3}>{formatNumber(item.value)}</Col>
                      </>
                    )}
                  </Row>
                </div>
              }
              key={item.identificator}
            >
              <Lot
                index={lotIndex}
                key={item.identificator}
                onDelete={handleDeleteLot}
                ticketIndex={index}
                ticketWillBePaid={isPaidWatch}
                ticketWillBeUnlimited={isUnlimetdWatch}
              />
            </Panel>
          );
        })}
      </Collapse>

      {openModalDelete?.modal === 'disable' && (
        <ModalWarning
          content='Atenção: Alguns ingressos deste lote já foram vendidos, por esse motivo o lote não pode ser excluído. Deseja desabilitar?'
          onCancel={() => setOpenModalDelete(null)}
          onContinue={() => disableLot()}
          open={openModalDelete.modal === 'disable'}
          title='Desabilitar lote'
        />
      )}

      {openModalDelete?.modal === 'delete' && (
        <ModalWarning
          content='Atenção: Esta ação não poderá ser desfeita. Deseja excluir este lote definitivamente?'
          onCancel={() => setOpenModalDelete(null)}
          onContinue={() => removeLot()}
          open={openModalDelete.modal === 'delete'}
          title='Deletar lote'
        />
      )}

      <Grid item xs={12} style={{ display: 'flex', marginTop: '16px' }}>
        <Button onClick={handleAddLot} type='primary' style={{ marginLeft: 'auto' }}>
          Adicionar Lote
        </Button>
      </Grid>
    </S.Wrapper>
  );
};

export default Ticket;
